/**
 * CoverageGapsPanel — lists INVENTORY rows with no matching TOOL.
 *
 * Extracted from AiUsageDashboard.tsx:630-741 (panel) + 1250-1293 (GapRow).
 * Correction applied on extraction: the two native <select> filters become
 * SearchableDropdown, per the platform's custom-controls-only rule.
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import type { ToolCatalogGap } from '../../../services/aiControlApi';

const INITIAL_GAP_ROWS = 10;

interface Props {
  gaps: ToolCatalogGap[];
  stubbing: string | null;
  stubError: string | null;
  onGenerateStub: (gap: ToolCatalogGap) => void;
  onDismissError: () => void;
}

const GapRow: React.FC<{
  gap: ToolCatalogGap;
  isStubbing: boolean;
  onGenerateStub: () => void;
}> = ({ gap, isStubbing, onGenerateStub }) => {
  const isWriteMethod = gap.method !== 'GET';
  const methodColor =
    gap.method === 'GET' ? 'bg-blue-100 text-blue-700' :
    gap.method === 'POST' ? 'bg-orange-100 text-orange-700' :
    gap.method === 'PUT' ? 'bg-rose-100 text-rose-700' :
    gap.method === 'DELETE' ? 'bg-rose-100 text-rose-700' :
    'bg-gray-100 text-gray-700';

  return (
    <tr className="hover:bg-stone-50">
      <td className="px-3 py-2">
        <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mr-1 ${methodColor}`}>
          {gap.method}
        </span>
        <span className="text-xs font-mono text-gray-800">{gap.path}</span>
      </td>
      <td className="px-3 py-2 text-xs text-gray-600">{gap.service}</td>
      <td className="px-3 py-2 hidden md:table-cell">
        <span className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{gap.domain}</span>
      </td>
      <td className="px-3 py-2 text-right">
        {isWriteMethod ? (
          <span className="text-[11px] text-gray-400" title="Write methods require explicit review — edit the tool row manually">
            Review required
          </span>
        ) : (
          <button
            type="button"
            onClick={onGenerateStub}
            disabled={isStubbing}
            className="min-h-[44px] px-2 text-[11px] text-emerald-600 hover:text-emerald-700 hover:underline font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {isStubbing ? 'Generating…' : 'Generate stub'}
          </button>
        )}
      </td>
    </tr>
  );
};

const CoverageGapsPanel: React.FC<Props> = ({
  gaps, stubbing, stubError, onGenerateStub, onDismissError,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [serviceFilter, setServiceFilter] = useState<string>('');
  const [methodFilter, setMethodFilter] = useState<string>('');

  const services = Array.from(new Set(gaps.map((g) => g.service))).sort();

  const filtered = gaps.filter((g) => {
    if (serviceFilter && g.service !== serviceFilter) return false;
    if (methodFilter && g.method !== methodFilter) return false;
    return true;
  });
  const visible = showAll ? filtered : filtered.slice(0, INITIAL_GAP_ROWS);

  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle size={14} className="text-rose-500" />
            Coverage Gaps
            <span className="text-[10px] text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded font-medium">
              {gaps.length} uncovered
            </span>
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Discovered endpoints the agent can't answer. "Generate stub" creates a draft tool row (coverage=stub).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-40">
            <SearchableDropdown
              options={[{ value: '', label: 'All services' }, ...services.map((s) => ({ value: s, label: s }))]}
              value={serviceFilter || ''}
              onChange={(v) => setServiceFilter(v || '')}
              placeholder="All services"
              searchPlaceholder="Search services…"
              compact
            />
          </div>
          <div className="w-36">
            <SearchableDropdown
              options={[
                { value: '', label: 'All methods' },
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'DELETE', label: 'DELETE' },
              ]}
              value={methodFilter || ''}
              onChange={(v) => setMethodFilter(v || '')}
              placeholder="All methods"
              searchPlaceholder="Search methods…"
              compact
            />
          </div>
        </div>
      </div>

      {stubError && (
        <div className="px-4 py-2 bg-rose-50 border-b border-rose-100 text-xs text-rose-700 flex items-center justify-between">
          <span><strong>Stub failed:</strong> {stubError}</span>
          <button
            type="button"
            onClick={onDismissError}
            className="min-h-[44px] px-2 text-rose-500 hover:text-rose-700"
          >
            ✕
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">
          {gaps.length === 0
            ? '🎉 No gaps — every discovered endpoint has a registered tool.'
            : 'No gaps match the current filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Endpoint</th>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Service</th>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide hidden md:table-cell">Domain</th>
                <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.map((g) => (
                <GapRow
                  key={g.sk}
                  gap={g}
                  isStubbing={stubbing === g.sk}
                  onGenerateStub={() => onGenerateStub(g)}
                />
              ))}
            </tbody>
          </table>
          {filtered.length > INITIAL_GAP_ROWS && (
            <div className="px-3 py-2 text-center border-t border-stone-100">
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className="min-h-[44px] px-3 text-[11px] text-blue-600 hover:underline font-medium"
              >
                {showAll
                  ? `Show top ${INITIAL_GAP_ROWS} only`
                  : `Show all ${filtered.length} gaps`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoverageGapsPanel;
